import { readonly, ref } from 'vue'
import { useLogger } from '~/composables/useLogger'
import { ToastType } from '~/enums'
import type { Toast } from './Toast'

interface ToastOptions {
  duration?: number
  description?: string
  action?: {
    label: string
    onClick: () => void
  }
}

const toasts = ref<Toast[]>([])

function generateRandomId(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
}

export function useToast() {
  const logger = useLogger('useToast')

  function show(type: ToastType, message: string, options: ToastOptions = {}): void {
    const { duration = 4000, description, action } = options

    const id = generateRandomId()

    toasts.value.unshift({
      id,
      type,
      message,
      ...(description !== undefined && description.length > 0 ? { description } : {}),
      ...(action ? { action } : {}),
      timeout: duration
    })

    if (duration > 0) {
      setTimeout(() => {
        remove(id)
      }, duration)
    }
  }

  function error(message: string, options: ToastOptions = {}): void {
    show(ToastType.ERROR, message, options)
    logger.error('error', { message })
  }

  function success(message: string, options: ToastOptions = {}): void {
    show(ToastType.SUCCESS, message, options)
  }

  function warning(message: string, options: ToastOptions = {}): void {
    show(ToastType.WARNING, message, options)
  }

  function info(message: string, options: ToastOptions = {}): void {
    show(ToastType.NEUTRAL, message, options)
  }

  function remove(id: string): void {
    toasts.value = toasts.value.filter((n) => n.id !== id)
  }

  return { toasts: readonly(toasts), show, remove, success, error, warning, info }
}
